// A1 notation, the addressing Excel already gives us for free.
//
// Word has to synthesise paragraph IDs (pA, pB, ...) and re-derive them on every
// extract, which is the most fragile part of that implementation: an edit shifts the
// document and a pending tool call's ID goes stale. Excel has none of that problem --
// "Sheet1!B7" is stable, human-readable, and means the same thing before and after an
// edit -- so this file is small on purpose. It exists only to convert between column
// letters and indices, which the Excel API makes us do by hand.

const A = "A".charCodeAt(0);

/** 0 -> "A", 25 -> "Z", 26 -> "AA". */
export function columnLetter(index: number): string {
  if (index < 0 || !Number.isInteger(index)) {
    throw new RangeError(`Column index must be a non-negative integer, got ${index}`);
  }
  let letters = "";
  let remaining = index;
  while (remaining >= 0) {
    letters = String.fromCharCode(A + (remaining % 26)) + letters;
    remaining = Math.floor(remaining / 26) - 1;
  }
  return letters;
}

/** "A" -> 0, "Z" -> 25, "AA" -> 26. Case-insensitive. */
export function columnIndex(letters: string): number {
  if (!/^[A-Za-z]+$/.test(letters)) {
    throw new RangeError(`Not a column reference: ${letters}`);
  }
  return (
    letters
      .toUpperCase()
      .split("")
      .reduce((total, letter) => total * 26 + (letter.charCodeAt(0) - A + 1), 0) - 1
  );
}

/** A single cell, e.g. (row 0, column 0) -> "A1". Rows and columns are 0-based. */
export function cellAddress(row: number, column: number): string {
  return `${columnLetter(column)}${row + 1}`;
}

/**
 * A rectangle, e.g. (0, 0, 3, 2) -> "A1:B3". Collapses to a single cell when the
 * rectangle is 1x1, because "A1" reads better than "A1:A1" in a prompt.
 */
export function rangeAddress(
  row: number,
  column: number,
  rowCount: number,
  columnCount: number
): string {
  const start = cellAddress(row, column);
  if (rowCount <= 1 && columnCount <= 1) return start;
  return `${start}:${cellAddress(row + rowCount - 1, column + columnCount - 1)}`;
}

/** Qualifies an address with its sheet, quoting the name when it needs it. */
export function qualify(sheetName: string, address: string): string {
  const needsQuotes = /[^A-Za-z0-9_]/.test(sheetName);
  const name = needsQuotes ? `'${sheetName.replace(/'/g, "''")}'` : sheetName;
  return `${name}!${address}`;
}

export interface QualifiedAddress {
  /** Undefined when the address named no sheet, meaning "the active one". */
  sheetName?: string;
  /** The A1 part, with any sheet prefix removed. */
  address: string;
}

/**
 * Splits "Sheet1!B7" or "'Q1 Actuals'!B7:D9" into its parts.
 *
 * The split is on the LAST "!" rather than the first, because a quoted sheet name may
 * legitimately contain one ('What?!'!A1). An unquoted name cannot, so this is safe in
 * both directions.
 */
export function parseQualified(reference: string): QualifiedAddress {
  const trimmed = reference.trim();
  const bang = trimmed.lastIndexOf("!");
  if (bang === -1) return { address: trimmed };

  const rawName = trimmed.slice(0, bang).trim();
  const address = trimmed.slice(bang + 1).trim();

  // Unwrap 'quoted name' and undouble any escaped apostrophes inside it.
  const quoted = rawName.startsWith("'") && rawName.endsWith("'") && rawName.length >= 2;
  const sheetName = quoted ? rawName.slice(1, -1).replace(/''/g, "'") : rawName;

  return sheetName ? { sheetName, address } : { address };
}
